import fhirpath from "fhirpath";
import r4Model from "fhirpath/fhir-context/r4/index.js";
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Extension,
} from "fhir/r4";
import type { Answers, Issue } from "./types";
export * from "./types";
export const EXT = {
  min: "http://hl7.org/fhir/StructureDefinition/minValue",
  max: "http://hl7.org/fhir/StructureDefinition/maxValue",
  help: "https://bitsandflames.dev/fhir/StructureDefinition/iron-question-help",
  control: "http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl",
  ordinal: "http://hl7.org/fhir/StructureDefinition/ordinalValue",
  calculated:
    "http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-calculatedExpression",
  unit: "http://hl7.org/fhir/StructureDefinition/questionnaire-unit",
};
export type SurveyState = { answers: Answers; repeats: Record<string, number> };
export const emptyState = (): SurveyState => ({ answers: {}, repeats: {} });
export const questionTypes = [
  "group",
  "display",
  "string",
  "text",
  "boolean",
  "integer",
  "decimal",
  "date",
  "dateTime",
  "time",
  "choice",
  "open-choice",
  "quantity",
  "url",
] as const;
export const clone = <T>(value: T): T => structuredClone(value);
export function flatten(items: QuestionnaireItem[] = []): QuestionnaireItem[] {
  return items.flatMap((i) => [i, ...flatten(i.item)]);
}
export function getExtension(item: { extension?: Extension[] }, url: string) {
  return item.extension?.find((e) => e.url === url);
}
export function setExtension<T extends { extension?: Extension[] }>(
  item: T,
  url: string,
  extension?: Omit<Extension, "url">,
): T {
  const list = (item.extension || []).filter((e) => e.url !== url);
  if (extension) list.push({ url, ...extension });
  return { ...item, extension: list.length ? list : undefined };
}
export function newItem(
  type: QuestionnaireItem["type"] = "string",
): QuestionnaireItem {
  return {
    linkId: `q-${crypto.randomUUID().slice(0, 8)}`,
    type,
    text:
      type === "group"
        ? "New section"
        : type === "display"
          ? "Instructions"
          : "Untitled question",
    ...(type === "group" ? { item: [] } : {}),
    ...(type === "choice" || type === "open-choice"
      ? {
          answerOption: [
            { valueString: "Option 1" },
            { valueString: "Option 2" },
          ],
        }
      : {}),
  };
}
export function updateItem(
  q: Questionnaire,
  id: string,
  update: (i: QuestionnaireItem) => QuestionnaireItem,
): Questionnaire {
  function walk(items: QuestionnaireItem[]): QuestionnaireItem[] {
    return items.map((i) =>
      i.linkId === id
        ? update(clone(i))
        : { ...i, ...(i.item ? { item: walk(i.item) } : {}) },
    );
  }
  return { ...q, item: walk(q.item || []) };
}
export function removeItem(q: Questionnaire, id: string): Questionnaire {
  const walk = (items: QuestionnaireItem[]): QuestionnaireItem[] =>
    items
      .filter((i) => i.linkId !== id)
      .map((i) => ({ ...i, ...(i.item ? { item: walk(i.item) } : {}) }));
  return { ...q, item: walk(q.item || []) };
}
export function addItem(
  q: Questionnaire,
  item: QuestionnaireItem,
  parent?: string,
): Questionnaire {
  return parent
    ? updateItem(q, parent, (i) => ({ ...i, item: [...(i.item || []), item] }))
    : { ...q, item: [...(q.item || []), item] };
}
export function moveItem(
  q: Questionnaire,
  id: string,
  delta: number,
): Questionnaire {
  function walk(items: QuestionnaireItem[]): QuestionnaireItem[] {
    const idx = items.findIndex((i) => i.linkId === id);
    if (idx >= 0) {
      const next = [...items];
      const target = idx + delta;
      if (target >= 0 && target < next.length)
        [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    }
    return items.map((i) => ({
      ...i,
      ...(i.item ? { item: walk(i.item) } : {}),
    }));
  }
  return { ...q, item: walk(q.item || []) };
}
export function duplicateItem(item: QuestionnaireItem) {
  const copied = clone(item);
  const ids = new Map<string, string>();
  flatten([copied]).forEach((i) => {
    const old = i.linkId;
    i.linkId = newItem(i.type).linkId;
    ids.set(old, i.linkId);
  });
  flatten([copied]).forEach((i) =>
    i.enableWhen?.forEach((c) => {
      c.question = ids.get(c.question) || c.question;
    }),
  );
  flatten([copied]).forEach((i) => {
    const expression = getExtension(i, EXT.calculated)?.valueExpression;
    if (expression?.expression)
      expression.expression = expression.expression.replace(
        /(linkId\s*=\s*)(['"])([^'"]+)\2/g,
        (match, prefix, quote, id) =>
          ids.has(id) ? `${prefix}${quote}${ids.get(id)}${quote}` : match,
      );
  });
  copied.text = `${copied.text || "Question"} (copy)`;
  return copied;
}
export function answerValue(a: QuestionnaireResponseItemAnswer): unknown {
  if (a.valueCoding)
    return `${a.valueCoding.system || ""}|${a.valueCoding.code || ""}`;
  if (a.valueQuantity) return a.valueQuantity.value;
  return Object.entries(a).find(([k]) => k.startsWith("value"))?.[1];
}
export function answerLabel(a: QuestionnaireResponseItemAnswer): string {
  if (a.valueCoding) return a.valueCoding.display || a.valueCoding.code || "";
  if (a.valueQuantity)
    return `${a.valueQuantity.value ?? ""} ${a.valueQuantity.unit || ""}`.trim();
  if (a.valueBoolean !== undefined) return a.valueBoolean ? "Yes" : "No";
  return String(answerValue(a) ?? "");
}
function nearestAnswers(
  state: SurveyState,
  id: string,
  path: string,
): [string, QuestionnaireResponseItemAnswer[]][] {
  const prefix = path.split("/").slice(0, -1);
  const candidates = Object.entries(state.answers).filter(([key]) => {
    const parts = key.split("/");
    return (
      parts.at(-1) === id &&
      prefix.every(
        (part, index) =>
          !/\[\d+\]$/.test(part) ||
          !parts[index]?.startsWith(part.replace(/\[\d+\]$/, "") + "[") ||
          parts[index] === part,
      )
    );
  });
  const score = (key: string) =>
    key.split("/").reduce((n, p, i) => n + (prefix[i] === p ? 1 : 0), 0);
  const best = Math.max(-1, ...candidates.map(([key]) => score(key)));
  return candidates.filter(([key]) => score(key) === best);
}
function conditionMatches(
  operator: string,
  actual: unknown,
  expected: unknown,
) {
  switch (operator) {
    case "=":
      return actual === expected;
    case "!=":
      return actual !== expected;
    case ">":
      return (
        typeof actual === typeof expected &&
        (actual as number) > (expected as number)
      );
    case "<":
      return (
        typeof actual === typeof expected &&
        (actual as number) < (expected as number)
      );
    case ">=":
      return (
        typeof actual === typeof expected &&
        (actual as number) >= (expected as number)
      );
    case "<=":
      return (
        typeof actual === typeof expected &&
        (actual as number) <= (expected as number)
      );
    default:
      return false;
  }
}
export function isEnabled(
  item: QuestionnaireItem,
  state: SurveyState,
  q: Questionnaire,
  path = item.linkId,
  seen = new Set<string>(),
): boolean {
  if (seen.has(path)) return false;
  if (!item.enableWhen?.length) return true;
  const visiting = new Set(seen);
  visiting.add(path);
  const all = flatten(q.item);
  const conditions = item.enableWhen.map((c) => {
    const source = all.find((i) => i.linkId === c.question);
    const answers = nearestAnswers(state, c.question, path).flatMap(
      ([sourcePath, raw]) => {
        const parts = sourcePath.split("/");
        const enabled =
          source &&
          isEnabled(source, state, q, sourcePath, visiting) &&
          parts.slice(0, -1).every((part, index) => {
            const parent = all.find(
              (i) => i.linkId === part.replace(/\[\d+\]$/, ""),
            );
            return (
              parent &&
              isEnabled(
                parent,
                state,
                q,
                parts.slice(0, index + 1).join("/"),
                visiting,
              )
            );
          });
        return enabled ? raw : [];
      },
    );
    if (c.operator === "exists") return answers.length > 0 === c.answerBoolean;
    if (!answers.length) return false;
    const expected = answerValue(
      Object.fromEntries(
        Object.entries(c)
          .filter(([k]) => k.startsWith("answer"))
          .map(([k, v]) => [k.replace("answer", "value"), v]),
      ),
    );
    return answers.some((a) =>
      conditionMatches(c.operator, answerValue(a), expected),
    );
  });
  return item.enableBehavior === "any"
    ? conditions.some(Boolean)
    : conditions.every(Boolean);
}
const supportedExtensions = new Set(Object.values(EXT));
export function validateQuestionnaire(q: Questionnaire): Issue[] {
  const issues: Issue[] = [];
  const all = flatten(q.item);
  const ids = new Set<string>();
  const error = (message: string, linkId?: string) =>
    issues.push({ severity: "error", message, linkId });
  if (q.resourceType !== "Questionnaire")
    error("The resource must be a FHIR R4 Questionnaire.");
  if (!q.title?.trim()) error("Give your questionnaire a title.");
  if (!all.some((i) => !["group", "display"].includes(i.type)))
    error("Add at least one question.");
  if (all.length > 300)
    error("The questionnaire exceeds the 300-item execution limit.");
  for (const i of all) {
    if (
      !i.linkId ||
      /[\[\]/]/.test(i.linkId) ||
      ["__proto__", "prototype", "constructor"].includes(i.linkId) ||
      ids.has(i.linkId)
    )
      error(
        "Each item needs a unique, non-reserved linkId without /, [ or ].",
        i.linkId,
      );
    ids.add(i.linkId);
    if (!i.text?.trim()) error("Enter question or section text.", i.linkId);
    if (!questionTypes.includes(i.type as (typeof questionTypes)[number]))
      error(`The runner does not support ${i.type} questions yet.`, i.linkId);
    if (
      i.type === "group" &&
      (i.initial?.length || i.answerOption || i.readOnly)
    )
      error(
        "Sections cannot have initial answers, answer options or read-only answers.",
        i.linkId,
      );
    if (i.type === "group" && !i.item?.length)
      error("Add questions to this section.", i.linkId);
    if (i.type !== "group" && i.item?.length)
      error(
        "Nested questions under answers are preserved on import but not supported by this runner.",
        i.linkId,
      );
    if (
      i.type === "display" &&
      (i.required ||
        i.repeats ||
        i.readOnly ||
        i.answerOption ||
        i.initial?.length)
    )
      error("Display items cannot have answer properties.", i.linkId);
    if (i.type === "display" && i.code?.length)
      error("Display items cannot have terminology codes.", i.linkId);
    if (i.answerOption?.length && i.initial?.length)
      error(
        "Use answerOption.initialSelected instead of initial values on choice questions.",
        i.linkId,
      );
    if (!i.repeats && (i.initial?.length || 0) > 1)
      error("Multiple initial values require a repeating question.", i.linkId);
    if (
      i.maxLength !== undefined &&
      (!["string", "text", "url"].includes(i.type) ||
        !Number.isInteger(i.maxLength) ||
        i.maxLength < 1)
    )
      error(
        "Maximum length must be a positive integer on a text or URL question.",
        i.linkId,
      );
    if (i.answerValueSet)
      error(
        "External ValueSets are preserved; resolve them to answerOption before publishing in this release.",
        i.linkId,
      );
    if (i.answerOption && !["choice", "open-choice"].includes(i.type))
      error("Answer options are supported on choice questions only.", i.linkId);
    if (["choice", "open-choice"].includes(i.type) && !i.answerOption?.length)
      error("Add answer choices.", i.linkId);
    if (
      i.answerOption?.some(
        (o) =>
          !("valueString" in o) &&
          !("valueCoding" in o) &&
          !("valueInteger" in o) &&
          !("valueDate" in o),
      )
    )
      error("Unsupported answer-option value type.", i.linkId);
    if (
      i.answerOption &&
      new Set(i.answerOption.map((o) => JSON.stringify(answerValue(o))))
        .size !== i.answerOption.length
    )
      error("Answer choices must be unique.", i.linkId);
    for (const option of i.answerOption || []) {
      if (
        option.valueCoding &&
        (!option.valueCoding.code || !option.valueCoding.system)
      )
        error("Coded choices need a system and code.", i.linkId);
      if (Object.keys(option).filter((k) => k.startsWith("value")).length !== 1)
        error("Each answer option must have exactly one value[x].", i.linkId);
      for (const e of option.extension || [])
        if (e.url !== EXT.ordinal || typeof e.valueDecimal !== "number")
          error(
            "Answer-option extensions currently support numeric ordinalValue only.",
            i.linkId,
          );
    }
    if (
      !i.repeats &&
      (i.answerOption?.filter((o) => o.initialSelected).length || 0) > 1
    )
      error(
        "Only one initial choice is allowed unless multiple answers are enabled.",
        i.linkId,
      );
    if (i.enableWhen && i.enableWhen.length > 1 && !i.enableBehavior)
      error("Choose ALL or ANY for multiple conditions.", i.linkId);
    for (const c of i.enableWhen || []) {
      if (
        !all.some(
          (a) =>
            a.linkId === c.question && !["group", "display"].includes(a.type),
        )
      )
        error("A condition refers to a missing question.", i.linkId);
      if (c.operator === "exists" && typeof c.answerBoolean !== "boolean")
        error("An exists condition needs a boolean.", i.linkId);
      if (c.question === i.linkId)
        error("A question cannot depend on itself.", i.linkId);
      if (Object.keys(c).filter((k) => k.startsWith("answer")).length !== 1)
        error("Each condition needs exactly one answer[x].", i.linkId);
    }
    if (i.modifierExtension?.length)
      error("Unknown modifier extensions prevent safe execution.", i.linkId);
    for (const e of i.extension || [])
      if (!supportedExtensions.has(e.url as (typeof EXT)[keyof typeof EXT]))
        error(
          `Unsupported extension: ${e.url}. It is preserved but must be reviewed before execution.`,
          i.linkId,
        );
    const help = getExtension(i, EXT.help);
    if (help && typeof help.valueString !== "string")
      error("Question help requires a string value.", i.linkId);
    const control = getExtension(i, EXT.control);
    if (control) {
      const code = control.valueCodeableConcept?.coding?.find(
        (c) => c.system === "http://hl7.org/fhir/questionnaire-item-control",
      )?.code;
      if (
        !(code === "group" && i.type === "group") &&
        !(
          code === "radio-button" &&
          ["choice", "open-choice"].includes(i.type) &&
          !i.repeats
        ) &&
        !(
          code === "check-box" &&
          ["choice", "open-choice"].includes(i.type) &&
          i.repeats
        )
      )
        error(
          "This itemControl is preserved but unsupported by the runner.",
          i.linkId,
        );
    }
    if (getExtension(i, EXT.ordinal))
      error(
        "Ordinal values belong on answer options in this runner.",
        i.linkId,
      );
    const unit = getExtension(i, EXT.unit);
    if (
      unit &&
      (i.type !== "quantity" ||
        !unit.valueCoding?.code ||
        !unit.valueCoding?.system)
    )
      error(
        "Units require a quantity question with a coding system and code.",
        i.linkId,
      );
    if (i.enableBehavior && !["all", "any"].includes(i.enableBehavior))
      error("Condition behavior must be all or any.", i.linkId);
    if (
      i.initial?.length &&
      validateAnswers(
        {
          resourceType: "Questionnaire",
          status: "draft",
          item: [
            {
              ...i,
              readOnly: false,
              enableWhen: undefined,
              extension: i.extension?.filter((e) => e.url !== EXT.calculated),
            },
          ],
        },
        { answers: { [i.linkId]: i.initial }, repeats: {} },
        false,
      ).length
    )
      error(
        "An initial answer has the wrong type or violates this question's constraints.",
        i.linkId,
      );
    const min = getExtension(i, EXT.min);
    const max = getExtension(i, EXT.max);
    for (const e of [min, max].filter(Boolean))
      if (
        !["integer", "decimal", "quantity"].includes(i.type) ||
        typeof (e!.valueDecimal ?? e!.valueInteger) !== "number"
      )
        error(
          "This runner supports min/max extensions only for numeric questions with numeric limits.",
          i.linkId,
        );
    if (
      min &&
      max &&
      Number(min.valueDecimal ?? min.valueInteger) >
        Number(max.valueDecimal ?? max.valueInteger)
    )
      error("The minimum cannot exceed the maximum.", i.linkId);
    const calc = getExtension(i, EXT.calculated)?.valueExpression;
    if (calc) {
      if (
        calc.language !== "text/fhirpath" ||
        !calc.expression ||
        calc.expression.length > 1500
      )
        error(
          "Calculations require a FHIRPath expression of up to 1,500 characters.",
          i.linkId,
        );
      else
        try {
          for (const match of calc.expression.matchAll(
            /linkId\s*=\s*(['"])([^'"]+)\1/g,
          ))
            if (!all.some((a) => a.linkId === match[2]))
              error(
                `Calculation refers to a missing question: ${match[2]}.`,
                i.linkId,
              );
          fhirpath.compile(calc.expression, r4Model);
        } catch {
          error("The calculated expression is not valid FHIRPath.", i.linkId);
        }
      if (!["integer", "decimal", "string", "boolean"].includes(i.type))
        error(
          "Calculated answers support integer, decimal, string and boolean.",
          i.linkId,
        );
      if (!i.readOnly)
        error("Calculated questions must be read-only.", i.linkId);
    } else if (i.readOnly && !i.initial?.length)
      error("Read-only questions need an initial value.", i.linkId);
  }
  // Include parent dependencies: a child cannot enable the section containing it.
  const deps = new Map<string, string[]>();
  function dependencyWalk(items: QuestionnaireItem[], ancestors: string[]) {
    items.forEach((i) => {
      deps.set(i.linkId, [
        ...ancestors,
        ...(i.enableWhen || []).map((c) => c.question).filter((q): q is string => typeof q === "string"),
        ...[
          ...(
            getExtension(i, EXT.calculated)?.valueExpression?.expression || ""
          ).matchAll(/linkId\s*=\s*(['"])([^'"]+)\1/g),
        ].map((match) => match[2]!),
      ]);
      dependencyWalk(i.item || [], [...ancestors, i.linkId]);
    });
  }
  dependencyWalk(q.item || [], []);
  const cycleCache = new Map<string, boolean>();
  const visit = (id: string, chain: Set<string>): boolean => {
    if (chain.has(id)) return true;
    if (cycleCache.has(id)) return cycleCache.get(id)!;
    const cycle = (deps.get(id) || []).some((d) =>
      visit(d, new Set([...chain, id])),
    );
    cycleCache.set(id, cycle);
    return cycle;
  };
  for (const id of deps.keys())
    if (visit(id, new Set()))
      error(
        "Conditional or calculated logic contains a circular dependency.",
        id,
      );
  if (q.implicitRules)
    error(
      "Questionnaires with implicit rules require a runner that understands those rules.",
    );
  if (q.subjectType?.length && !q.subjectType.includes("Patient"))
    error("Clinic surveys require Patient as a supported subject type.");
  if (q.modifierExtension?.length)
    error("Questionnaire modifier extensions are unsupported.");
  if (q.contained?.length)
    error(
      "Contained resources are preserved but not supported for execution in this release.",
    );
  if (q.extension?.length)
    for (const e of q.extension)
      error(
        `Questionnaire-level extension preserved but unsupported for execution: ${e.url}`,
      );
  return issues;
}
export function toResponse(
  q: Questionnaire,
  state: SurveyState,
  context: {
    id?: string;
    patient?: string;
    encounter?: string;
    status?: QuestionnaireResponse["status"];
  } = {},
): QuestionnaireResponse {
  function walk(
    items: QuestionnaireItem[],
    parent = "",
  ): QuestionnaireResponseItem[] {
    return items.flatMap<QuestionnaireResponseItem>((i) => {
      const path = parent ? `${parent}/${i.linkId}` : i.linkId;
      if (!isEnabled(i, state, q, path) || i.type === "display") return [];
      if (i.type === "group") {
        const count = i.repeats ? state.repeats[path] || 1 : 1;
        return Array.from({ length: count }, (_, n) => ({
          linkId: i.linkId,
          text: i.text,
          item: walk(i.item || [], i.repeats ? `${path}[${n}]` : path),
        })).filter((g) => g.item.length > 0);
      }
      const answer = Array.isArray(state.answers[path])
        ? state.answers[path]
        : [];
      return answer.length
        ? [{ linkId: i.linkId, text: i.text, answer: clone(answer) }]
        : [];
    });
  }
  return {
    resourceType: "QuestionnaireResponse",
    ...(context.id ? { id: context.id } : {}),
    questionnaire: `${q.url || `Questionnaire/${q.id}`}|${q.version || "1"}`,
    status: context.status || "in-progress",
    authored: new Date().toISOString(),
    ...(context.patient ? { subject: { reference: context.patient } } : {}),
    ...(context.encounter
      ? { encounter: { reference: context.encounter } }
      : {}),
    item: walk(q.item || []),
  };
}
export function fromResponse(
  q: Questionnaire,
  response?: QuestionnaireResponse,
): SurveyState {
  const state = emptyState();
  function walk(
    defs: QuestionnaireItem[],
    items: QuestionnaireResponseItem[],
    parent = "",
  ) {
    for (const d of defs) {
      const path = parent ? `${parent}/${d.linkId}` : d.linkId;
      const matches = items.filter((i) => i.linkId === d.linkId);
      if (d.type === "group") {
        if (d.repeats) state.repeats[path] = Math.max(1, matches.length);
        (matches.length ? matches : [{ linkId: d.linkId, item: [] }]).forEach(
          (i, n) =>
            walk(
              d.item || [],
              i.item || [],
              d.repeats ? `${path}[${n}]` : path,
            ),
        );
      } else if (matches[0]?.answer)
        state.answers[path] = clone(matches[0].answer);
      else if (d.initial?.length) state.answers[path] = clone(d.initial);
      else if (d.answerOption?.some((o) => o.initialSelected))
        state.answers[path] = d.answerOption
          .filter((o) => o.initialSelected)
          .map(({ initialSelected: _, extension: __, ...answer }) => answer);
    }
  }
  walk(q.item || [], response?.item || []);
  return state;
}
function calculationResult(
  q: Questionnaire,
  input: SurveyState,
): { state: SurveyState; issues: Issue[] } {
  const state = clone(input);
  const targets: {
    item: QuestionnaireItem;
    path: string;
    expression: string;
  }[] = [];
  function collect(items: QuestionnaireItem[], parent = "") {
    for (const item of items) {
      const path = parent ? `${parent}/${item.linkId}` : item.linkId;
      if (item.type === "group") {
        const count = item.repeats ? state.repeats[path] || 1 : 1;
        if (!Number.isInteger(count) || count < 1 || count > 20) continue;
        for (let n = 0; n < count; n++)
          collect(item.item || [], item.repeats ? `${path}[${n}]` : path);
      } else {
        const expression = getExtension(item, EXT.calculated)?.valueExpression
          ?.expression;
        if (expression) {
          delete state.answers[path];
          targets.push({ item, path, expression });
        } else if (item.readOnly) {
          delete state.answers[path];
          if (item.initial?.length) state.answers[path] = clone(item.initial);
        }
      }
    }
  }
  collect(q.item || []);
  const dependencies = new Map(
    targets.map((t) => [
      t.item.linkId,
      [...t.expression.matchAll(/linkId\s*=\s*(['"])([^'"]+)\1/g)].map(
        (match) => match[2]!,
      ),
    ]),
  );
  const cyclic = (id: string, seen: Set<string>): boolean =>
    seen.has(id) ||
    (dependencies.get(id) || []).some((next) =>
      cyclic(next, new Set([...seen, id])),
    );
  if (targets.some((target) => cyclic(target.item.linkId, new Set())))
    return {
      state,
      issues: [
        {
          severity: "error",
          message:
            "Calculations contain a circular dependency. Review the questionnaire.",
        },
      ],
    };
  let issues: Issue[] = [];
  // Re-evaluate until stable so forward references do not depend on visual order.
  for (let pass = 0; pass <= targets.length; pass++) {
    const previous = JSON.stringify(targets.map((t) => state.answers[t.path]));
    issues = [];
    for (const { item, path, expression } of targets) {
      delete state.answers[path];
      if (!isEnabled(item, state, q, path)) continue;
      try {
        const response = toResponse(q, state);
        const values = fhirpath.evaluate(
          response,
          expression,
          { resource: response, questionnaire: q },
          r4Model,
          { async: false },
        );
        if (!values.length) continue;
        const value = values[0];
        const valid =
          values.length === 1 &&
          ((["integer", "decimal"].includes(item.type) &&
            typeof value === "number" &&
            Number.isFinite(value) &&
            (item.type !== "integer" || Number.isInteger(value))) ||
            (item.type === "string" && typeof value === "string") ||
            (item.type === "boolean" && typeof value === "boolean"));
        if (!valid)
          throw Error("Calculated result has the wrong type or cardinality.");
        const field = {
          integer: "valueInteger",
          decimal: "valueDecimal",
          string: "valueString",
          boolean: "valueBoolean",
        }[item.type as "integer"];
        state.answers[path] = [{ [field]: value }];
      } catch {
        issues.push({
          severity: "error",
          linkId: path,
          message:
            "The calculation failed. Ask the questionnaire author to review its expression.",
        });
      }
    }
    if (JSON.stringify(targets.map((t) => state.answers[t.path])) === previous)
      return { state, issues };
  }
  return {
    state,
    issues: [
      ...issues,
      {
        severity: "error",
        message:
          "Calculations did not stabilize. Review circular dependencies.",
      },
    ],
  };
}
export function calculatedState(
  q: Questionnaire,
  input: SurveyState,
): SurveyState {
  return calculationResult(q, input).state;
}
export function validateAnswers(
  q: Questionnaire,
  input: SurveyState,
  complete = true,
): Issue[] {
  const { state, issues } = calculationResult(q, input);
  const error = (message: string, path: string) =>
    issues.push({ severity: "error", linkId: path, message });
  function hasAnswer(items: QuestionnaireItem[], parent: string): boolean {
    return items.some((i) => {
      const path = `${parent}/${i.linkId}`;
      if (!isEnabled(i, state, q, path) || i.type === "display") return false;
      if (i.type !== "group") return !!state.answers[path]?.length;
      const count = i.repeats ? state.repeats[path] || 1 : 1;
      return Array.from({ length: Math.min(count, 20) }, (_, n) =>
        hasAnswer(i.item || [], i.repeats ? `${path}[${n}]` : path),
      ).some(Boolean);
    });
  }
  function walk(items: QuestionnaireItem[], parent = "") {
    for (const i of items) {
      const path = parent ? `${parent}/${i.linkId}` : i.linkId;
      if (!isEnabled(i, state, q, path) || i.type === "display") continue;
      if (i.type === "group") {
        const count = i.repeats ? state.repeats[path] || 1 : 1;
        if (count < 1 || count > 20 || !Number.isInteger(count))
          error("Repeating sections allow 1–20 entries.", path);
        else
          for (let n = 0; n < count; n++) {
            const instance = i.repeats ? `${path}[${n}]` : path;
            if (complete && i.required && !hasAnswer(i.item || [], instance))
              error("Answer at least one question in this section.", instance);
            walk(i.item || [], instance);
          }
        continue;
      }
      const answers = Array.isArray(state.answers[path])
        ? state.answers[path]
        : [];
      if (complete && i.required && !answers.length)
        error("Please answer this question.", path);
      if (!i.repeats && answers.length > 1)
        error("Only one answer is allowed.", path);
      if (answers.length > 50) error("Too many answers.", path);
      const expected = (
        {
          string: ["valueString"],
          text: ["valueString"],
          url: ["valueUri"],
          integer: ["valueInteger"],
          decimal: ["valueDecimal"],
          boolean: ["valueBoolean"],
          date: ["valueDate"],
          dateTime: ["valueDateTime"],
          time: ["valueTime"],
          quantity: ["valueQuantity"],
          choice: ["valueString", "valueCoding", "valueInteger", "valueDate"],
          "open-choice": [
            "valueString",
            "valueCoding",
            "valueInteger",
            "valueDate",
          ],
        } as Record<string, string[]>
      )[i.type];
      for (const a of answers) {
        const fields = Object.keys(a).filter((k) => k.startsWith("value"));
        if (fields.length !== 1 || !expected?.includes(fields[0]!)) {
          error("The answer has the wrong FHIR value type.", path);
          continue;
        }
        const value = answerValue(a);
        if (
          (["string", "text", "url", "date", "dateTime", "time"].includes(
            i.type,
          ) &&
            typeof value !== "string") ||
          (a.valueCoding &&
            (typeof a.valueCoding !== "object" ||
              typeof a.valueCoding.code !== "string" ||
              typeof a.valueCoding.system !== "string")) ||
          (a.valueQuantity && typeof a.valueQuantity !== "object")
        )
          error("The answer has an invalid FHIR value.", path);
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (typeof value === "string" && !value.trim())
        )
          error("Enter an answer or clear this field.", path);
        if (
          ["integer", "decimal", "quantity"].includes(i.type) &&
          (typeof value !== "number" ||
            !Number.isFinite(value) ||
            (i.type === "integer" && !Number.isInteger(value)))
        )
          error("Enter a valid number.", path);
        const unit = getExtension(i, EXT.unit)?.valueCoding;
        if (
          i.type === "quantity" &&
          unit &&
          (a.valueQuantity?.code !== unit.code ||
            a.valueQuantity?.system !== unit.system)
        )
          error("The quantity must use the configured unit.", path);
        if (i.type === "boolean" && typeof value !== "boolean")
          error("Choose Yes or No.", path);
        if (
          i.type === "date" &&
          (typeof value !== "string" ||
            !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
            !Number.isFinite(Date.parse(value)) ||
            new Date(value).toISOString().slice(0, 10) !== value)
        )
          error("Enter a valid date.", path);
        if (
          i.type === "dateTime" &&
          (typeof value !== "string" ||
            !/^\d{4}-\d{2}-\d{2}T/.test(value) ||
            !Number.isFinite(Date.parse(value)))
        )
          error("Enter a valid date and time.", path);
        if (
          i.type === "time" &&
          (typeof value !== "string" ||
            !/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?$/.test(value))
        )
          error("Enter a valid time.", path);
        if (i.type === "url") {
          try {
            const url = new URL(String(value));
            if (!["https:", "http:"].includes(url.protocol)) throw Error();
          } catch {
            error("Enter an http or https URL.", path);
          }
        }
        if (i.maxLength && String(value).length > i.maxLength)
          error(`Use no more than ${i.maxLength} characters.`, path);
        const min = getExtension(i, EXT.min);
        const max = getExtension(i, EXT.max);
        if (
          typeof value === "number" &&
          min &&
          value < Number(min.valueDecimal ?? min.valueInteger)
        )
          error(`Minimum: ${min.valueDecimal ?? min.valueInteger}.`, path);
        if (
          typeof value === "number" &&
          max &&
          value > Number(max.valueDecimal ?? max.valueInteger)
        )
          error(`Maximum: ${max.valueDecimal ?? max.valueInteger}.`, path);
        if (
          i.type === "choice" &&
          !i.answerOption?.some((o) => answerValue(o) === value)
        )
          error("Select a listed answer.", path);
      }
    }
  }
  walk(q.item || []);
  return issues;
}
export function importQuestionnaire(value: unknown): Questionnaire {
  if (
    !value ||
    typeof value !== "object" ||
    (value as Questionnaire).resourceType !== "Questionnaire"
  )
    throw Error("Import a FHIR R4 Questionnaire JSON resource.");
  const q = clone(value as Questionnaire);
  let count = 0;
  function inspect(items: unknown, depth: number) {
    if (items === undefined) return;
    if (!Array.isArray(items) || depth > 10)
      throw Error("Invalid item structure or nesting deeper than 10 levels.");
    for (const item of items) {
      if (
        ++count > 300 ||
        !item ||
        typeof item !== "object" ||
        typeof item.linkId !== "string" ||
        typeof item.type !== "string" ||
        ["__proto__", "prototype", "constructor"].includes(item.linkId)
      )
        throw Error(
          "Each item needs a non-reserved linkId and type; at most 300 items are supported.",
        );
      for (const key of ["text", "prefix"])
        if (item[key] !== undefined && typeof item[key] !== "string")
          throw Error(`${key} must be text.`);
      for (const key of ["required", "repeats", "readOnly"])
        if (item[key] !== undefined && typeof item[key] !== "boolean")
          throw Error(`${key} must be a boolean.`);
      for (const key of [
        "extension",
        "modifierExtension",
        "answerOption",
        "enableWhen",
        "initial",
        "code",
      ])
        if (
          item[key] !== undefined &&
          (!Array.isArray(item[key]) ||
            item[key].some(
              (e: unknown) => !e || typeof e !== "object" || Array.isArray(e),
            ))
        )
          throw Error(`${key} must be an array of objects.`);
      for (const e of item.extension || [])
        if (typeof e.url !== "string") throw Error("Extensions need a URL.");
      const valueObjects = [
        ...(item.answerOption || []),
        ...(item.initial || []),
        ...(item.enableWhen || []),
      ];
      for (const answer of valueObjects) {
        for (const [key, value] of Object.entries(answer)) {
          if (!key.startsWith("value") && !key.startsWith("answer")) continue;
          if (
            [
              "valueCoding",
              "answerCoding",
              "valueQuantity",
              "answerQuantity",
              "valueReference",
              "valueAttachment",
            ].includes(key)
          ) {
            if (!value || typeof value !== "object" || Array.isArray(value))
              throw Error(`${key} must be an object.`);
            if (key.endsWith("Coding"))
              for (const [field, content] of Object.entries(value))
                if (
                  ["system", "code", "display"].includes(field) &&
                  typeof content !== "string"
                )
                  throw Error(`Coding.${field} must be text.`);
          } else if (key.endsWith("Boolean")) {
            if (typeof value !== "boolean")
              throw Error(`${key} must be a boolean.`);
          } else if (key.endsWith("Integer") || key.endsWith("Decimal")) {
            if (typeof value !== "number" || !Number.isFinite(value))
              throw Error(`${key} must be a number.`);
          } else if (typeof value !== "string")
            throw Error(`${key} must be text.`);
        }
      }
      for (const c of item.enableWhen || [])
        if (
          typeof c.question !== "string" ||
          !["exists", "=", "!=", ">", "<", ">=", "<="].includes(c.operator)
        )
          throw Error("Invalid enableWhen condition.");
      inspect(item.item, depth + 1);
    }
  }
  for (const key of ["title", "description", "url", "language", "publisher"])
    if (
      q[key as keyof Questionnaire] !== undefined &&
      typeof q[key as keyof Questionnaire] !== "string"
    )
      throw Error(`${key} must be text.`);
  for (const key of ["extension", "modifierExtension", "contained"])
    if (
      q[key as keyof Questionnaire] !== undefined &&
      !Array.isArray(q[key as keyof Questionnaire])
    )
      throw Error(`${key} must be an array.`);
  inspect(q.item, 0);
  return q;
}
